import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import { signInWithGoogle } from '../../services/firebase';
import { useForm, validationRules } from '../../hooks/useForm';
import Alert from '../../components/Common/Alert';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit
  } = useForm(
    { username: '', password: '' },
    {
      username: [validationRules.required, validationRules.usernameOrEmail],
      password: [validationRules.required]
    }
  );

  const onSubmit = async (formData) => {
    try {
      console.log('Attempting login with data:', formData);
      const response = await authService.login(formData);
      console.log('Login response:', response);

      if (response.user.user_type !== 'seller') {
        setAlert({
          type: 'danger',
          message: 'This portal is only for sellers. Please register as a seller or use the correct login portal.'
        });
        return;
      }

      // Fetch complete profile data after successful login
      const profileData = await authService.getProfile();
      onLogin(profileData);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed. Please check your credentials.';
      let alertType = 'danger';

      if (error.response?.data?.error) {
        const errorType = error.response.data.error;
        if (errorType === 'account_under_review') {
          errorMessage = error.response.data.message || 'We are reviewing your account details. You will receive an email notification of your account status within 3 to 5 business days.';
          alertType = 'warning';
        } else if (errorType === 'account_rejected') {
          errorMessage = error.response.data.message || 'Your seller account verification was rejected. Please contact support for more information.';
          alertType = 'danger';
        } else if (errorType === 'account_inactive') {
          errorMessage = error.response.data.message || 'Your seller account is inactive. Please contact support.';
          alertType = 'danger';
        } else {
          errorMessage = error.response.data.error;
        }
      } else if (error.response?.data?.non_field_errors) {
         errorMessage = 'Invalid username/email or password. Please check your credentials.';
      } else if (error.response?.status === 403) {
        errorMessage = error.response?.data?.message || 'Your seller account is not yet verified. Please wait for verification or contact support.';
        alertType = 'warning';
      }

      setAlert({
        type: alertType,
        message: errorMessage
      });
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      setAlert(null);
      const googleResult = await signInWithGoogle();
      const response = await authService.googleAuth(googleResult.accessToken);

      if (response.is_new_user) {
        showAppDownloadPopup();
        return;
      } else {
        if (response.user.user_type !== 'seller') {
          setAlert({
            type: 'danger',
            message: 'This portal is only for sellers. Please register as a seller first.'
          });
          return;
        }
        const profileData = await authService.getProfile();
        onLogin(profileData);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      setAlert({
        type: 'danger',
        message: error.message || 'Google sign-in failed. Please try again.'
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const showAppDownloadPopup = () => {
    setAlert({
      type: 'info',
      message: (
        <div>
          <strong>New User Registration</strong><br />
          To register as a seller, please download our WhatNew Live app.
          <div className="mt-2">
            <a href="https://play.google.com/store/apps/details?id=com.shop.whatnew" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success me-2">
              Google Play
            </a>
          </div>
        </div>
      )
    });
  };

  return (
    <div className="modern-login-container">
      {/* Animated Background */}
      <div className="login-bg-gradient"></div>
      <div className="login-bg-overlay"></div>
      
      {/* Floating Particles */}
      <div className="floating-particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <div className="container position-relative" style={{ zIndex: 10 }}>
        <div className="row justify-content-center align-items-center min-vh-100 py-5">
          <div className="col-12 col-md-10 col-lg-8 col-xl-6">
            
            {/* Logo and Branding */}
            <div className="text-center mb-5">
              <div className="d-inline-flex align-items-center justify-content-center mb-4" 
                   style={{ 
                     width: '80px', 
                     height: '80px', 
                     background: 'linear-gradient(135deg, #e50914 0%, #b00710 100%)',
                     borderRadius: '20px',
                     boxShadow: '0 10px 40px rgba(229, 9, 20, 0.4)',
                     animation: 'pulse 2s ease-in-out infinite'
                   }}>
                <i className="bi bi-shop-window text-white" style={{ fontSize: '2.5rem' }}></i>
              </div>
              <h1 className="text-white fw-bold mb-2" style={{ fontSize: '2.5rem', letterSpacing: '-1px' }}>
                WhatNew Seller
              </h1>
              <p className="text-secondary mb-0" style={{ fontSize: '1.1rem' }}>
                Manage your live commerce empire
              </p>
            </div>

            {/* Main Login Card */}
            <div className="login-glass-card">
              {/* Card Header */}
              <div className="text-center mb-4 pb-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill"
                     style={{ 
                       background: 'rgba(229, 9, 20, 0.15)',
                       border: '1px solid rgba(229, 9, 20, 0.3)'
                     }}>
                  <i className="bi bi-shield-check text-danger"></i>
                  <span className="text-white small fw-semibold">SECURE SELLER ACCESS</span>
                </div>
              </div>

              {alert && (
                <Alert
                  type={alert.type}
                  message={alert.message}
                  onClose={() => setAlert(null)}
                  className="mb-4"
                />
              )}

              <form onSubmit={(e) => {
                e.preventDefault();
                if (!isSubmitting) handleSubmit(onSubmit);
              }}>
                {/* Username Field */}
                <div className="mb-4">
                  <label htmlFor="username" className="form-label text-white fw-semibold mb-3">
                    <i className="bi bi-person-circle me-2 text-danger"></i>
                    Email or Username
                  </label>
                  <div className="modern-input-wrapper">
                    <input
                      type="text"
                      className={`modern-input ${errors.username ? 'is-invalid' : ''}`}
                      id="username"
                      value={values.username}
                      onChange={(e) => handleChange('username', e.target.value)}
                      placeholder="Enter your email or username"
                    />
                    {errors.username && (
                      <div className="invalid-feedback d-block mt-2">
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {errors.username}
                      </div>
                    )}
                  </div>
                </div>

                {/* Password Field */}
                <div className="mb-4">
                  <label htmlFor="password" className="form-label text-white fw-semibold mb-3">
                    <i className="bi bi-lock-fill me-2 text-danger"></i>
                    Password
                  </label>
                  <div className="modern-input-wrapper">
                    <input
                      type="password"
                      className={`modern-input ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      value={values.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="Enter your password"
                    />
                    {errors.password && (
                      <div className="invalid-feedback d-block mt-2">
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {errors.password}
                      </div>
                    )}
                  </div>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  className="modern-login-btn w-100 mb-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Authenticating...
                    </>
                  ) : (
                    <>
                      <span>Sign In to Dashboard</span>
                      <i className="bi bi-arrow-right ms-2"></i>
                    </>
                  )}
                </button>

                {/* Divider */}
                <div className="position-relative my-4">
                  <hr style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  <span className="position-absolute top-50 start-50 translate-middle px-3 text-secondary small"
                        style={{ background: '#0f1420' }}>
                    OR
                  </span>
                </div>

                {/* Google Sign In */}
                <button
                  type="button"
                  className="google-signin-btn w-100"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || isSubmitting}
                >
                  {googleLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-google me-2"></i>
                      Continue with Google
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Bottom Info */}
            <div className="text-center mt-4">
              <p className="text-secondary small mb-0" style={{ opacity: 0.5 }}>
                <i className="bi bi-shield-lock me-1"></i>
                Protected by enterprise-grade security
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;




